import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

import * as authApi from "@/services/api/auth";
import type { UserPublic } from "@/services/api/types";

interface AuthState {
  /** The authenticated principal, or null when anonymous. */
  user: UserPublic | null;
  /** True while the startup `/auth/me` bootstrap is in flight. */
  isLoading: boolean;
  login: (email: string, password: string) => Promise<UserPublic>;
  logout: () => Promise<void>;
  setUser: (user: UserPublic | null) => void;
}

const AuthContext = createContext<AuthState | null>(null);

interface AuthProviderProps {
  children: ReactNode;
  /** Test seam: when provided, skips the `/auth/me` bootstrap. */
  initialUser?: UserPublic | null;
}

export function AuthProvider({ children, initialUser }: AuthProviderProps) {
  const [user, setUser] = useState<UserPublic | null>(initialUser ?? null);
  const [isLoading, setIsLoading] = useState(initialUser !== undefined ? false : true);

  useEffect(() => {
    if (initialUser !== undefined) {
      return;
    }
    let cancelled = false;
    authApi
      .me()
      .then((current) => {
        if (!cancelled) setUser(current);
      })
      .catch(() => {
        // 401 here simply means "anonymous"; guards handle the redirect.
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [initialUser]);

  const login = useCallback(async (email: string, password: string) => {
    const current = await authApi.login(email, password);
    setUser(current);
    return current;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
    }
  }, []);

  // Idle session timeout (item 15): each user's timeout comes from their
  // profile (idle_timeout_minutes, capped at 120 server-side). Kiosk profiles
  // are exempt. Activity resets the timer; expiry logs the user out.
  useEffect(() => {
    if (!user || user.session_profile === "kiosk") {
      return;
    }
    const minutes = Math.min(Math.max(user.idle_timeout_minutes ?? 30, 1), 120);
    let timer: ReturnType<typeof setTimeout> | undefined;
    const reset = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        void logout();
      }, minutes * 60_000);
    };
    const events: (keyof WindowEventMap)[] = [
      "mousemove",
      "keydown",
      "click",
      "scroll",
      "touchstart",
    ];
    for (const event of events) window.addEventListener(event, reset);
    reset();
    return () => {
      if (timer) clearTimeout(timer);
      for (const event of events) window.removeEventListener(event, reset);
    };
  }, [user, logout]);

  const value = useMemo(
    () => ({ user, isLoading, login, logout, setUser }),
    [user, isLoading, login, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
